import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { voucherVerify } from "@/lib/api";

const trackPixel = (event, params = {}) => {
    if (typeof window !== "undefined" && window.fbq) window.fbq("track", event, params);
};

export default function Riscatta() {
    const [searchParams] = useSearchParams();
    const [code, setCode] = useState(searchParams.get("c") || "");
    const [busy, setBusy] = useState(false);
    const [voucher, setVoucher] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        trackPixel("PageView");
        if (searchParams.get("c")) {
            // Auto-verify se codice passato da email link
            setTimeout(() => verify(searchParams.get("c")), 500);
        }
    }, []);

    const verify = async (c) => {
        setError("");
        setVoucher(null);
        setBusy(true);
        try {
            const r = await voucherVerify(c);
            setVoucher(r);
            trackPixel("Lead", { content_type: "voucher_verified", value: (r.amount_cents || 0) / 100, currency: "EUR" });
            toast.success("Voucher valido! Ora scegli la data.");
        } catch (err) {
            const detail = err?.response?.data?.detail || "Errore verifica";
            setError(detail);
            toast.error(detail);
        } finally {
            setBusy(false);
        }
    };

    const proceedToBooking = () => {
        if (!voucher) return;
        // Salva codice in sessionStorage per essere applicato al prossimo checkout
        sessionStorage.setItem("velora_voucher_code", voucher.code);
        sessionStorage.setItem("velora_voucher_product", voucher.product_id);
        // Redirige alla home con anchor sui prodotti
        window.location.href = `/#prodotti?voucher=${encodeURIComponent(voucher.code)}`;
    };

    return (
        <div className="min-h-screen bg-[#0a0b0d] text-[#F4F2ED] flex flex-col" data-testid="page-riscatta">
            <header className="border-b-4 border-[#E2231A]">
                <div className="max-w-4xl mx-auto px-5 md:px-8 py-6 flex items-center justify-between">
                    <a href="/" className="hover:opacity-80 transition"><img src="/logo/velora-horizontal.png" alt="VELORA JDM" className="h-8 md:h-10" /></a>
                    <a href="/voucher" className="text-xs uppercase tracking-widest text-[#9AA2AD] hover:text-[#F4F2ED]">Compra voucher →</a>
                </div>
            </header>

            <main className="flex-1 max-w-2xl mx-auto px-5 md:px-8 py-12 w-full">
                <div className="text-center mb-10">
                    <div className="text-5xl mb-4">🎁</div>
                    <h1 className="font-display text-3xl md:text-5xl uppercase mb-3">Riscatta il tuo <span className="text-[#E2231A]">voucher</span></h1>
                    <p className="text-[#c8ccd1]">Inserisci il codice ricevuto via email o su carta regalo.</p>
                </div>

                {!voucher && (
                    <form onSubmit={(e) => { e.preventDefault(); verify(code); }} className="space-y-5" data-testid="voucher-verify-form">
                        <input
                            type="text"
                            value={code}
                            onChange={e => setCode(e.target.value.toUpperCase())}
                            data-testid="input-voucher-code"
                            placeholder="VELORA-XXXX-XXXX"
                            className="w-full bg-[#15181C] border-2 border-[#9AA2AD]/20 focus:border-[#E2231A] outline-none px-5 py-5 text-center font-display text-2xl md:text-3xl tracking-[0.3em] uppercase"
                            required
                            minLength={13}
                            autoFocus
                        />
                        {error && <div className="text-[#E2231A] text-sm text-center" data-testid="voucher-error">⚠ {error}</div>}
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="btn-verify-voucher"
                            className="w-full bg-[#E2231A] hover:bg-[#C11B15] disabled:opacity-50 text-[#F4F2ED] py-4 uppercase tracking-widest font-display text-lg"
                        >{busy ? "Verifica…" : "Verifica codice"}</button>
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] text-center pt-2">
                            Il codice è valido 12 mesi dall'acquisto · Riscattabile una sola volta
                        </div>
                    </form>
                )}

                {voucher && (
                    <div data-testid="voucher-valid">
                        <div className="bg-gradient-to-br from-[#15181C] via-[#0f2b0f] to-[#15181C] border-2 border-[#16a34a] p-6 md:p-8 mb-6">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-[#16a34a] mb-1">✓ Voucher valido</div>
                                    <div className="font-display text-2xl">€{voucher.amount_cents / 100}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1">Codice</div>
                                    <div className="font-mono text-xs">{voucher.code}</div>
                                </div>
                            </div>
                            <div className="text-xs text-[#c8ccd1] mb-2">
                                Valido per: <b className="uppercase">{voucher.tier === "combo" ? "Combo Duello (EVO X + WRX)" : "Singola in Pista (EVO X o WRX)"}</b>
                            </div>
                            <div className="text-xs text-[#9AA2AD]">Scade il {(voucher.expires_at || "").slice(0, 10)}</div>
                            {voucher.message && (
                                <div className="mt-4 pt-4 border-t border-[#16a34a]/20 italic text-sm text-[#c8ccd1]">
                                    "{voucher.message}"
                                </div>
                            )}
                        </div>

                        <div className="border border-[#E2231A]/40 bg-[#E2231A]/5 p-5 text-sm mb-6">
                            <div className="font-display text-sm uppercase tracking-widest mb-2 text-[#E2231A]">Prossimo passo</div>
                            <p className="text-[#c8ccd1]">
                                Ora scegli la <b>data dell'evento</b> e completa la registrazione (patente, manleva, anagrafica).
                                Nessun pagamento aggiuntivo dovuto — il voucher copre l'intero importo.
                            </p>
                        </div>

                        <button
                            onClick={proceedToBooking}
                            data-testid="btn-proceed-booking"
                            className="w-full bg-[#E2231A] hover:bg-[#C11B15] text-[#F4F2ED] py-4 uppercase tracking-widest font-display text-lg"
                        >🏁 Scegli data e prenota</button>

                        <button
                            onClick={() => { setVoucher(null); setCode(""); }}
                            className="w-full mt-3 text-xs text-[#9AA2AD] hover:text-[#F4F2ED] uppercase tracking-widest py-2"
                        >← Cambia codice</button>
                    </div>
                )}
            </main>

            <footer className="border-t border-[#9AA2AD]/20 py-6 text-center text-xs text-[#9AA2AD]">
                VELORA JDM · Marchio di MAS S.r.l. · Assistenza: <a href="mailto:info@velorajdm.com" className="hover:text-[#F4F2ED]">info@velorajdm.com</a>
            </footer>
        </div>
    );
}
